<template>
	<div
		:id="containerId"
		:class="['editor-container', { 'is-active': isActive }]"
		:style="editorStyle"
		@focusin="HandleFocusIn"
		@focusout="HandleFocusOut"
	>
		<div
			:id="toolbarId"
			class="editor-toolbar"
		>
			<select class="ql-size">
				<option
					v-for="size in normalizedSizeOptions"
					:key="size"
					:value="size"
				>
					{{ size.replace('px', '') }}
				</option>
			</select>

			<button class="ql-bold">N</button>
			<button class="ql-italic"></button>
			<button class="ql-underline"></button>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-indent"
					value="+1"
				></button>
				<button
					class="ql-indent"
					value="-1"
				></button>
			</div>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-align"
					value=""
				></button>
				<button
					class="ql-align"
					value="center"
				></button>
				<button
					class="ql-align"
					value="right"
				></button>
				<button
					class="ql-align"
					value="justify"
				></button>
			</div>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-list"
					value="ordered"
				></button>
				<button
					class="ql-list"
					value="bullet"
				></button>
			</div>
		</div>
		<quill-editor
			:options="QuillOptions"
			ref="quill"
			@ready="QuillReadyHandler"
			v-model="content"
			spellcheck="false"
		/>
	</div>
</template>

<script>
	import { quillEditor, Quill } from 'vue-quill-editor'

	let editorInstanceCounter = 0

	const Size = Quill.import('attributors/style/size')
	Size.whitelist = [
		'8px',
		'10px',
		'12px',
		'14px',
		'16px',
		'18px',
		'20px',
		'24px',
		'28px',
		'32px',
		'36px',
		'48px',
	]
	Quill.register(Size, true)

	import 'quill/dist/quill.core.css'
	import 'quill/dist/quill.snow.css'
	import 'quill/dist/quill.bubble.css'

	export default {
		name: 'Editor',
		components: { quillEditor },
		props: {
			value: {
				type: String,
				default: '',
			},
			resetSelectionOnEmpty: {
				type: Boolean,
				default: true,
			},
			maxlength: {
				type: Number,
				default: null,
			},
			toolbarPreset: {
				type: String,
				default: 'full',
			},
			sizeOptions: {
				type: Array,
				default: () => [
					'8px',
					'10px',
					'12px',
					'14px',
					'16px',
					'18px',
					'20px',
					'24px',
					'28px',
					'32px',
					'36px',
					'48px',
				],
			},
		},
		computed: {
			isCompactToolbar() {
				return this.toolbarPreset === 'stamps'
			},
			editorStyle() {
				const style = {
					'--primary': '#616161',
				}

				if (this.toolbarPreset === 'stamps' && this.isActive) {
					style['--hr'] = 'var(--newColor)'
				}

				return style
			},
			normalizedSizeOptions() {
				const defaultSizes = [
					'8px',
					'10px',
					'12px',
					'14px',
					'16px',
					'18px',
					'20px',
					'24px',
					'28px',
					'32px',
					'36px',
					'48px',
				]

				const validSizes = this.sizeOptions
					.filter(size => typeof size === 'string' && /^(\d+)px$/.test(size))
					.filter((size, index, arr) => arr.indexOf(size) === index)

				if (validSizes.length === 0) {
					return defaultSizes
				}

				return validSizes
			},
		},
		data() {
			const instanceId = ++editorInstanceCounter
			const toolbarId = `toolbar-${instanceId}`
			const containerId = `editor-container-${instanceId}`

			return {
				toolbarId,
				containerId,
				content: this.value,
				isActive: false,
				defaultFontSize: this.toolbarPreset === 'stamps' ? '10px' : '16px',
				primaryColor: { '--primary': '#616161' },
				isResettingContent: false,
				timer: null,
				QuillOptions: {
					modules: {
						toolbar: `#${toolbarId}`,
					},
					theme: 'snow',
					placeholder: '',
				},
			}
		},
		watch: {
			normalizedSizeOptions: {
				handler(sizes) {
					if (!sizes.includes(this.defaultFontSize)) {
						this.defaultFontSize = sizes[0]
					}
				},
				immediate: true,
			},
			value(val) {
				if (val !== this.content) {
					this.content = val
					this.contentFormatted = false
					this.$nextTick(() => {
						if (this.$refs.quill && this.$refs.quill.getLength) {
							this.InitializeContent(this.$refs.quill)
						}
					})
				}
				const hasValue = !!val && val.trim().length > 0
				this.isActive = hasValue || this.HasFocusInsideEditor()
			},
			content(val) {
				this.$emit('input', val)
			},
		},
		methods: {
			NormalizeToSingleLine(quillInstance) {
				if (!quillInstance || !quillInstance.getContents || !quillInstance.setContents)
					return false

				const delta = quillInstance.getContents()
				if (!delta || !Array.isArray(delta.ops)) return false

				let changed = false
				const normalizedOps = []

				delta.ops.forEach(op => {
					if (typeof op.insert !== 'string') {
						normalizedOps.push(op)
						return
					}

					const normalizedInsert = op.insert.replace(/[\r\n]+/g, ' ')
					if (normalizedInsert !== op.insert) changed = true

					if (!normalizedInsert) return

					if (op.attributes) {
						normalizedOps.push({
							insert: normalizedInsert,
							attributes: { ...op.attributes },
						})
						return
					}

					normalizedOps.push({ insert: normalizedInsert })
				})

				if (!changed) return false

				const lastOp = normalizedOps[normalizedOps.length - 1]
				if (lastOp && typeof lastOp.insert === 'string') {
					const trimmed = lastOp.insert.replace(/\s+$/, '')
					if (trimmed) {
						lastOp.insert = trimmed
					} else {
						normalizedOps.pop()
					}
				}

				normalizedOps.push({ insert: '\n' })

				const selection = quillInstance.getSelection()
				quillInstance.setContents({ ops: normalizedOps }, 'api')

				if (selection) {
					const maxIndex = Math.max(0, quillInstance.getLength() - 1)
					quillInstance.setSelection(
						Math.min(selection.index, maxIndex),
						0,
						'silent'
					)
				}

				return true
			},
			HasFocusInsideEditor() {
				if (typeof document === 'undefined' || !this.$el) return false
				return this.$el.contains(document.activeElement)
			},
			HandleFocusIn() {
				this.isActive = true
				this.$emit('focus')
			},
			HandleFocusOut() {
				setTimeout(() => {
					if (this.HasFocusInsideEditor()) return

					const hasText = this.$refs.quill && this.$refs.quill.getText
						? this.$refs.quill.getText().trim().length > 0
						: !!this.content && this.content.trim().length > 0

					this.isActive = hasText
					this.$emit('blur')
				}, 0)
			},
			FixPickerLabel(quillInstance, size) {
				const toolbar = quillInstance.getModule('toolbar')
				if (toolbar && toolbar.container) {
					const pickerLabel = toolbar.container.querySelector(
						'.ql-picker.ql-size .ql-picker-label'
					)
					if (pickerLabel) {
						pickerLabel.setAttribute('data-value', size)
						pickerLabel.setAttribute(
							'data-label',
							size.replace('px', '')
						)
					}
				}
			},
			GetPickerLabelValue(quillInstance) {
				const toolbar = quillInstance.getModule('toolbar')
				if (toolbar && toolbar.container) {
					const pickerLabel = toolbar.container.querySelector(
						'.ql-picker.ql-size .ql-picker-label'
					)
					if (pickerLabel)
						return pickerLabel.getAttribute('data-value')
				}
				return null
			},
			ApplyDefaultSizeToContent(quillInstance, fontSize, formatSource = 'silent') {
				if (!quillInstance || !quillInstance.getLength) return

				const length = quillInstance.getLength()
				if (length <= 1) return

				const delta = quillInstance.getContents()
				let index = 0

				delta.ops.forEach(op => {
					const opLength =
						typeof op.insert === 'string' ? op.insert.length : 1
					if (!op.attributes || !op.attributes.size) {
						quillInstance.formatText(
							index,
							opLength,
							'size',
							fontSize,
							formatSource
						)
					}
					index += opLength
				})

				quillInstance.format('size', fontSize, formatSource)
			},
			HasTextWithoutSize(quillInstance) {
				if (!quillInstance || !quillInstance.getLength) return false

				const length = quillInstance.getLength()
				if (length <= 1) return false

				const delta = quillInstance.getContents()
				return delta.ops.some((op) => {
					if (typeof op.insert !== 'string') return false
					if (op.insert.trim().length === 0) return false
					return !op.attributes || !op.attributes.size
				})
			},
			GetContentFontSize(delta) {
				for (const op of delta.ops) {
					if (op.attributes && op.attributes.size) {
						return op.attributes.size
					}
				}
				return null
			},
			InitializeContent(quillInstance) {
				if (this.contentFormatted) return

				const htmlContent = this.value || this.content

				if (!htmlContent || quillInstance.getLength() <= 1) {
					this.contentFormatted = true
					quillInstance.format('size', this.defaultFontSize)
					return
				}

				this.contentFormatted = true

				const delta = quillInstance.clipboard.convert(htmlContent)

				const detectedSize =
					this.GetContentFontSize(delta) || this.defaultFontSize

				quillInstance.setContents(delta, 'silent')
				this.ApplyDefaultSizeToContent(quillInstance, detectedSize)
			},
			QuillReadyHandler(quillInstance) {
				this.$refs.quill = quillInstance
				this.contentFormatted = false

				if (this.toolbarPreset === 'stamps') {
					const keyboard = quillInstance.getModule('keyboard')
					if (keyboard && keyboard.addBinding) {
						keyboard.addBinding({ key: 13 }, () => false)
					}

					if (quillInstance.root && quillInstance.root.addEventListener) {
						quillInstance.root.addEventListener('paste', event => {
							const items = event.clipboardData && event.clipboardData.items
							if (!items || !items.length) return

							const hasImage = Array.from(items).some(item =>
								item.kind === 'file' && /^image\//.test(item.type)
							)

							if (hasImage) {
								event.preventDefault()
							}
						})

						quillInstance.root.addEventListener('drop', event => {
							const files = event.dataTransfer && event.dataTransfer.files
							if (!files || !files.length) return

							const hasImage = Array.from(files).some(file =>
								/^image\//.test(file.type)
							)

							if (hasImage) {
								event.preventDefault()
							}
						})
					}
				}

				const clipboard = quillInstance.getModule('clipboard')
				if (clipboard) {
					const originalConvert = clipboard.convert.bind(clipboard)
					clipboard.convert = html => {
						const delta = originalConvert(html)
						if (delta && delta.ops) {
							delta.ops = delta.ops
								.map(op => {
									if (
										this.toolbarPreset === 'stamps' &&
										typeof op.insert === 'string'
									) {
										return {
											...op,
											insert: op.insert.replace(/[\r\n]+/g, ' '),
										}
									}
									return op
								})
								.filter(op => {
									if (this.toolbarPreset !== 'stamps') return true
									return !(op && op.insert && op.insert.image)
								})
								.map(op => {
								if (op.attributes) {
									const newAttrs = { ...op.attributes }
									delete newAttrs.color
									delete newAttrs.background
									return { ...op, attributes: newAttrs }
								}
								return op
							})
						}
						return delta
					}
				}

				this.$nextTick(() => {
					this.InitializeContent(quillInstance)
					this.isActive = quillInstance.getText && quillInstance.getText().trim().length > 0
				})

				setTimeout(() => {
					if (quillInstance.getLength() <= 1) {
						this.FixPickerLabel(quillInstance, this.defaultFontSize)
					}
				}, 0)

				quillInstance.on('selection-change', range => {
					const hasText = quillInstance.getText && quillInstance.getText().trim().length > 0
					const hasFocusInsideEditor = this.HasFocusInsideEditor()
					this.isActive = !!range || hasText || hasFocusInsideEditor

					if (!range) return
					if (quillInstance.getLength() <= 1) {
						setTimeout(() => {
							const dataValue =
								this.GetPickerLabelValue(quillInstance) ||
								this.defaultFontSize
							this.FixPickerLabel(quillInstance, dataValue)
						}, 0)
					}
				})

				quillInstance.on('text-change', (delta, oldDelta, source) => {
					const hasText = quillInstance.getText && quillInstance.getText().trim().length > 0
					const hasFocusInsideEditor = this.HasFocusInsideEditor()
					this.isActive = hasText || hasFocusInsideEditor

					if (this.maxlength && quillInstance.getLength && (quillInstance.getLength() - 1) > this.maxlength) {
						const extra = (quillInstance.getLength() - 1) - this.maxlength
						const deleteIndex = this.maxlength
						quillInstance.deleteText(deleteIndex, extra, 'api')
						return
					}

					if (source === 'api') {
						if (this.toolbarPreset === 'stamps') {
							return
						}

						this.$nextTick(() => {
							this.InitializeContent(quillInstance)
						})
						return
					}

					if (source === 'user') {
						const range = quillInstance.getSelection()
						if (range && range.length === 0) {
							const [leaf] = quillInstance.getLeaf(range.index)
							if (leaf && !leaf.parent.formats().size) {
								quillInstance.format(
									'size',
									this.defaultFontSize
								)
							}
						}

						if (this.toolbarPreset === 'stamps' && this.HasTextWithoutSize(quillInstance)) {
							this.ApplyDefaultSizeToContent(quillInstance, this.defaultFontSize, 'api')
							this.FixPickerLabel(quillInstance, this.defaultFontSize)
						}
					}

					if (this.isResettingContent) return

					clearTimeout(this.timer)

					this.timer = setTimeout(() => {
						this.SaveDebounced()
					}, 1000)
				})
			},
			SanitizeContent(content) {
				const isEmptyContent = `<p><br></p>`

				return content.replace(isEmptyContent, '<br>')
			},
			Save() {
				if (
					this.$refs.quill &&
					this.$refs.quill.root &&
					this.$refs.quill.root.innerHTML
				) {
					let content = this.$refs.quill.root.innerHTML

					if (content === '<p><br></p>') {
						content = ''
					}

					this.$emit('save', this.SanitizeContent(content))
				}
			},
			SaveDebounced() {
				const text = this.$refs.quill.getText().trim()

				if (text === '') {
					this.isResettingContent = true

					setTimeout(() => {
						if (this.resetSelectionOnEmpty) {
							this.$refs.quill.setSelection(0, 0)
							this.$refs.quill.format('size', this.defaultFontSize)
						} else {
							this.FixPickerLabel(this.$refs.quill, this.defaultFontSize)
						}
						this.isResettingContent = false
					}, 0)
				}

				this.Save()
			},
			ToolbarFixing() {
				// Bold fixing
				const boldButton = this.$el.querySelector('.ql-bold')
				if (boldButton) {
					boldButton.innerHTML = 'N'
				}
				// Underline fixing
				const underlineButton = this.$el.querySelector('.ql-underline')
				if (underlineButton) {
					underlineButton.innerHTML = 'S'
				}
			},
		},
		mounted() {
			this.ToolbarFixing()
		},
	}
</script>

<style lang="scss" scoped>
	.editor-container {
		height: 100% !important;

		--selectedColor: #2196f3;

		.ql-snow {
			border-color: var(--hr) !important;
		}
		.editor-toolbar.ql-toolbar,
		.editor-toolbar.ql-toolbar.ql-snow,
		:deep(.ql-snow.ql-toolbar),
		:deep(.ql-snow .ql-toolbar) {
			border-color: var(--hr) !important;
			// border: 1px solid var(--hr) !important;
			border-radius: 8px 8px 0 0;
			color: var(--color-font);

			button,
			span.ql-picker {
				box-shadow: unset;
				color: var(--primary);

				.ql-picker {
					color: var(--primary);
				}

				.ql-fill {
					fill: var(--primary);
				}

				.ql-stroke {
					stroke: var(--primary);
				}

				&.ql-active {
					background-color: unset;
					color: var(--selectedColor);

					.ql-picker {
						color: var(--selectedColor);
					}

					.ql-fill {
						fill: var(--selectedColor);
					}

					.ql-stroke {
						stroke: var(--selectedColor);
					}
				}

				&:hover {
					box-shadow: unset;
					background-color: unset;
					color: var(--primary);

					.ql-picker {
						color: var(--titleFontColor);
					}

					.ql-fill {
						fill: var(--titleFontColor);
					}

					.ql-stroke {
						stroke: var(--titleFontColor);
					}
				}
			}

			.ql-font.ql-picker {
				width: 120px;
			}

			.ql-picker {
				&.ql-expanded {
					.ql-picker-label {
						border-color: var(--hr) !important;
					}
				}

				.ql-picker-label {
					&.ql-active {
						background-color: unset;
						color: var(--selectedColor);
					}

					&:hover {
						background-color: unset;
						color: var(--titleFontColor);
					}
				}

				.ql-picker-options {
					padding: 0;
					border-color: var(--hr) !important;
					color: var(--font-color);

					.ql-stroke {
						color: var(--font-color);
					}

					.ql-picker-item {
						background-color: var(--dark-theme);
						padding: 5px 8px;

						&:hover {
							color: var(--titleFontColor);
						}

						&.ql-selected {
							color: var(--selectedColor);
						}
					}
				}
			}

			// Toolbar fixing
			.ql-bold,
			.ql-underline {
				font-family: sans-serif;
				font-size: 16px;
				width: 24px;
				height: 24px;
				padding: 0;
				display: flex !important;
				align-items: center !important;
				justify-content: center !important;
			}

			.ql-bold {
				font-weight: bold;
				&:hover {
					color: var(--titleFontColor);
				}
			}

			.ql-underline {
				text-decoration: underline !important;
				&:hover {
					color: var(--titleFontColor);
				}
			}

			// Extending QuillJS
			.ql-snow .ql-picker.ql-size .ql-picker-label::before,
			.ql-snow .ql-picker.ql-size .ql-picker-item::before {
				content: attr(data-value);
			}

			.ql-font-roboto {
				font-family: 'Roboto', sans-serif;
			}
			.ql-font-arial {
				font-family: Arial, Helvetica, sans-serif;
			}
			.ql-font-open_sans {
				font-family: 'Open Sans', sans-serif;
			}

			.ql-editor.ql-blank::before {
				left: unset !important;
				right: unset !important;
			}
		}
	}

	:deep(.editor-toolbar.ql-toolbar) {
		box-shadow: unset !important;
		border-color: var(--hr) !important;
	}

	:deep(.ql-container),
	:deep(.ql-container.ql-snow) {
		border: 1px solid var(--hr) !important;
		border-radius: 0 0 8px 8px;
		color: var(--titleFontColor) !important;
		box-shadow: unset !important;
		padding: 0 !important;
		.ql-editor {
			height: 54vh;
			overflow: auto;
			background-color: unset !important;
			color: var(--color-font) !important;
			caret-color: var(--color-font) !important;

			&.ql-blank::before {
				padding-left: 1rem !important;
			}
		}
	}
</style>
